import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import UtilBar from "@/components/UtilBar";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import QuoteModal from "@/components/QuoteModal";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import heroImg from "@/assets/wire-brushed-oak-dustless-sanding-refinishing-brighton-mi.webp";
import dustlessImg from "@/assets/pallmann-spider-planetary-sander-dustless-refinishing-michigan.webp";
import usePageSEO from "@/hooks/usePageSEO";
import PageFAQ from "@/components/PageFAQ";
import { refinishingFAQs } from "@/data/faqData";
import RelatedServices from "@/components/RelatedServices";
import GoogleReviewsCarousel from "@/components/GoogleReviewsCarousel";
import FullServiceCallout from "@/components/FullServiceCallout";

const fadeUp = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] as const },
};

const logisticsCards = [
  {
    icon: "🛋️",
    title: "Furniture & Trim Details",
    desc: "We handle the logistics. Carefully moving your furniture and protecting every baseboard, so you don't have to worry about a thing.",
  },
  {
    icon: "🛡️",
    title: "Environmental Protection",
    desc: "Beyond our 98% containment vacuums, we hang specialized barriers to seal off the workspace from the rest of your home.",
  },
  {
    icon: "⏱️",
    title: "Timeline & Curing",
    desc: "Whether we use instant-cure UV technology or traditional oil finishes, we communicate the exact timeline upfront. No surprises.",
  },
  {
    icon: "📖",
    title: "Maintenance Education",
    desc: "We don't just finish your floor and leave. We teach you exactly how to maintain your specific finish so it stays beautiful for decades.",
  },
];

const finishes = [
  {
    name: "Instant-Cure UV Hardwax Oils",
    tag: "Minimal Downtime",
    desc: "The pinnacle of modern finishing technology. We sand, color, and finish your floor, then instantly cure it with advanced UV light. You can walk on your floor the exact second we turn off the machine. No waiting, no tiptoeing around your own home.",
  },
  {
    name: "Bona HD & Loba Waterborne",
    desc: "Commercial-grade, two-component finishes built for extreme durability. Fast cure times, low VOCs, and a clarity that lets the wood's natural beauty shine through.",
  },
  {
    name: "Rubio Monocoat 2C",
    desc: "A plant-based hardwax oil that colors and protects in a single application. The result is a stunning matte, natural-wood feel that we chose specifically for clients who want their floors to look and feel like raw timber. Professional-grade protection included.",
  },
  {
    name: "Pallmann Magic Oil & Odie's Oil",
    desc: "Ultra-premium penetrating oils that nourish the wood from the inside out. We recommend these for clients who value spot-repairability. If a scratch happens five years from now, we can touch up that exact spot without resanding the entire floor.",
  },
];

const Refinishing = () => {
  usePageSEO({
    title: "Hardwood Floor Refinishing | Dustless Sand & Finish",
    description: "Dustless hardwood floor refinishing — sand & finish process, 2-component waterborne, 98% containment. NWFA Certified Sand & Finisher.",
    path: "/refinishing",
    // Service schema baked into static HTML via generate-seo-html.mjs
  });

  return (
    <div className="min-h-screen">
      <UtilBar />
      <SiteHeader />
      <main>

      {/* ══ 1 · HERO ══ */}
      <section className="relative h-[70svh] min-h-[480px] max-h-[720px] flex items-end overflow-hidden">
        <PageBreadcrumb variant="overlay" items={[
          { label: "Home", to: "/" },
          { label: "Services" },
          { label: "Refinishing" },
        ]} />
        <div className="absolute inset-0">
          <img width={1920} height={1080} loading="eager" decoding="async" fetchPriority="high"
            src={heroImg}
            alt="Close up of a wire brushed oak floor in a dark brown finish after refinishing, Brighton, MI"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/40 to-foreground/10" />

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-10 pb-10 md:pb-16 px-5 md:px-10 lg:px-16 max-w-4xl"
        >
          <p className="text-[10px] md:text-[11px] font-semibold tracking-[.14em] uppercase text-primary mb-3 md:mb-4 flex items-center gap-2">
            <span className="w-7 h-px bg-primary inline-block" />
            Dust Control Refinishing · Metro Detroit, Michigan
          </p>
          <h1 className="font-display text-[clamp(30px,6.5vw,72px)] font-semibold leading-[1.05] tracking-tight text-primary-foreground">
            Dustless Hardwood Floor Refinishing  Across Metro Detroit
          </h1>
          <p className="mt-4 md:mt-6 text-[13px] md:text-[15px] font-light text-primary-foreground/60 max-w-lg leading-relaxed">
            No sanding process is completely clean. 98% dust containment is a science. Industrial grade setups and instant cure UV finishes by Michigan's NWFA Certified team.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-6 md:mt-8">
            <QuoteModal
              trigger={
                <button className="bg-primary text-primary-foreground px-7 py-3 rounded-full text-sm font-medium hover:bg-[hsl(var(--gold-400))] transition-colors text-center">
                  Schedule a Call →
                </button>
              }
            />
            <a
              href="tel:2483258853"
              className="border border-primary-foreground/25 text-primary-foreground px-7 py-3 rounded-full text-sm font-medium hover:border-primary-foreground/60 transition-colors text-center"
            >
              Call (248) 325-8853
            </a>
          </div>
          <p className="mt-5 text-[13px] md:text-sm font-light text-primary-foreground/60 max-w-lg leading-relaxed">
            Older floor with history? See our <Link to="/floor-restoration" className="text-primary hover:underline">older floor restoration</Link>.
          </p>
        </motion.div>
      </section>

      {/* ══ 2 · THE MYTH OF DUSTLESS ══ */}
      <section className="bg-foreground py-16 md:py-24 lg:py-32">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14 items-center">
            <motion.div {...fadeUp}>
              <p className="text-[10px] md:text-[11px] font-semibold tracking-[.18em] uppercase text-primary mb-4">
                The Inspector's Difference
              </p>
              <h2 className="font-display text-[clamp(26px,4vw,44px)] font-semibold text-primary-foreground leading-[1.12]">
                The Truth About{" "}
                <em className="italic font-normal text-[hsl(var(--gold-300))]">"Dustless"</em> Systems.
              </h2>
              <p className="mt-5 text-sm md:text-[15px] font-light text-muted-foreground/80 leading-relaxed max-w-lg">
                Many contractors use the word "dustless" as a marketing gimmick. The reality? No system captures 100% of airborne dust. A standard contractor strapping a shop vac to a belt sander might only contain 70% to 80% of the particles.
              </p>
              <p className="mt-4 text-sm md:text-[15px] font-light text-muted-foreground/80 leading-relaxed max-w-lg">
                We utilize Planetary Sanding Technology. Unlike traditional belt machines, our multi disc Pallmann Spider is fully dust routed and vacuum integrated. Microscopic particles are captured at the source before they can settle in your home. That is 98% containment. Backed by physical barriers that protect every room.
              </p>
              <div className="flex flex-wrap gap-2 mt-6">
                {["98% Containment", "Planetary Sanding", "HVAC Protection", "Sealed Barriers"].map((chip) => (
                  <span
                    key={chip}
                    className="text-[9px] font-bold tracking-[.1em] uppercase text-[hsl(var(--gold-200))] border border-primary-foreground/10 rounded-md px-3 py-1.5 bg-primary-foreground/[.04]"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </motion.div>

            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.15 }} className="relative">
              <div className="rounded-2xl overflow-hidden relative group">
                <img width={800} height={534} loading="lazy"
                  src={dustlessImg}
                  alt="Professional Pallmann Spider planetary sander with integrated dust routing connected to a high powered vacuum system for a dustless wood floor refinishing project"
                  className="w-full aspect-[4/3] object-cover transition-all duration-700 ease-out grayscale-[20%] contrast-[1.1] group-hover:grayscale-0 group-hover:scale-[1.04]"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ 3 · PREMIUM FINISHES & UV TECH ══ */}
      <section className="bg-background py-14 md:py-20 lg:py-28">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-10 md:gap-16 items-start">
            <motion.div {...fadeUp} className="lg:sticky lg:top-24">
              <p className="text-[10px] md:text-[11px] font-semibold tracking-[.18em] uppercase text-primary mb-4">
                Finish Specification
              </p>
              <h2 className="font-display text-[clamp(26px,4.5vw,48px)] font-semibold text-foreground leading-[1.1]">
                Specify Your Perfect{" "}
                <em className="italic font-normal text-primary">Finish.</em>
              </h2>
              <p className="mt-4 text-sm md:text-[15px] font-light text-muted-foreground leading-relaxed">
                We consult with you to specify the exact finish based on your home's traffic, aesthetic desires, and downtime requirements. Refinishing <Link to="/reclaimed-barnwood" className="text-primary hover:text-foreground underline underline-offset-4 decoration-primary/40 hover:decoration-primary transition-colors">reclaimed barnwood flooring</Link> requires a different approach than sanding a standard site-finished floor  the character marks, nail holes, and softer earlywood on century-old material demand a gentler abrasive sequence and, in most cases, an oil or hard-wax finish rather than a heavy film build.
              </p>
            </motion.div>

            <div className="flex flex-col gap-3">
              {finishes.map((finish, i) => (
                <motion.div
                  key={finish.name}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.6 }}
                  className="border border-border/50 rounded-2xl p-6 md:p-8 hover:bg-muted/40 transition-colors duration-300"
                >
                  <div className="flex items-start gap-4">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs font-bold mt-0.5">
                      {i + 1}
                    </span>
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-display text-lg md:text-xl font-semibold text-foreground leading-snug">
                          {finish.name}
                        </h4>
                        {finish.tag && (
                          <span className="text-[8px] font-bold tracking-[.12em] uppercase bg-primary/15 text-primary rounded-full px-2.5 py-1">
                            {finish.tag}
                          </span>
                        )}
                      </div>
                      <p className="text-[13px] font-light text-muted-foreground leading-relaxed">
                        {finish.desc}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ 4 · END-TO-END LOGISTICS ══ */}
      <section className="bg-foreground py-14 md:py-20 lg:py-28">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          <motion.div {...fadeUp} className="mb-8 md:mb-12 max-w-2xl">
            <p className="text-[11px] font-semibold tracking-[.1em] uppercase text-[hsl(var(--gold-400))] mb-2 flex items-center gap-2">
              Full-Service Logistics
              <span className="flex-1 h-px bg-border" />
            </p>
            <h2 className="font-display text-[clamp(28px,5vw,52px)] font-semibold text-primary-foreground leading-[1.1]">
              We Manage Everything.{" "}
              <em className="italic font-normal text-[hsl(var(--gold-300))]">You Relax.</em>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {logisticsCards.map((card, i) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.6 }}
                className="border border-primary-foreground/10 rounded-2xl p-6 md:p-8 flex flex-col group hover:bg-primary-foreground/[.06] transition-colors duration-500"
              >
                <div className="text-3xl mb-4">{card.icon}</div>
                <h4 className="font-display text-lg md:text-xl font-semibold text-primary-foreground mb-3 leading-snug transition-colors duration-500">
                  {card.title}
                </h4>
                <p className="text-[12px] md:text-[13px] font-light text-primary-foreground/50 leading-relaxed flex-grow transition-colors duration-500">
                  {card.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ COST GUIDE LINK ══ */}
      <section className="bg-muted/30 py-10 md:py-14 border-t border-border">
        <div className="max-w-[900px] mx-auto px-5 md:px-12 text-center">
          <Link
            to="/refinishing-cost"
            className="inline-flex items-center gap-2 text-[14px] md:text-[15px] font-semibold text-primary hover:text-foreground transition-colors group"
          >
            See our complete Michigan hardwood floor refinishing cost guide
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      {/* ══ FULL-SERVICE REFINISHING ══ */}
      <FullServiceCallout variant="refinishing" />

      {/* ══ 5 · CTA BANNER ══ */}
      <section className="bg-primary py-10 md:py-14">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-display text-[clamp(24px,3vw,36px)] font-semibold text-primary-foreground leading-[1.1] mb-2">
              Breathe easy. Let the experts restore your floors.
            </h3>
            <p className="text-[13px] font-light text-primary-foreground/65 leading-relaxed">
              Connect directly with Michigan's NWFA Certified team to discuss your refinishing project and finish options.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0 w-full md:w-auto">
            <QuoteModal
              trigger={
                <button className="bg-primary-foreground text-primary px-7 py-3.5 rounded-xl text-xs font-bold tracking-[.05em] uppercase hover:-translate-y-px transition-transform whitespace-nowrap text-center">
                  Schedule a Consultation →
                </button>
              }
            />
            <a
              href="tel:2483258853"
              className="border border-primary-foreground/30 text-primary-foreground px-7 py-3.5 rounded-xl text-xs font-semibold tracking-[.05em] hover:border-primary-foreground hover:text-primary-foreground transition-colors whitespace-nowrap text-center"
            >
              📞 (248) 325-8853
            </a>
          </div>
        </div>
      </section>

        <RelatedServices
          heading="Refinishing is just the beginning."
          subheading="Related Services"
          links={[
            { title: "Hardwood Installation", desc: "Starting fresh? Our NWFA Certified Installers specify and install wide-plank hardwood engineered for Michigan's climate.", to: "/installation", tag: "Service" },
            { title: "Subfloor Leveling & Repair", desc: "Before we sand, we evaluate. Uneven subfloors cause finish failures. We correct the foundation first.", to: "/subfloor-leveling", tag: "Service" },
            { title: "Environment & Acclimation Science", desc: "Understand how Michigan's humidity extremes affect your hardwood and why proper HVAC control matters.", to: "/environment-acclimation", tag: "Technical" },
            { title: "Michigan Plank Flooring", desc: "Need a bespoke color or species? We manufacture custom wide-plank floors with European White Oak and domestic hardwoods.", to: "/michigan-plank-flooring", tag: "Brand" },
            { title: "NWFA Certified Inspections", desc: "Floor failing after a refinish by another contractor? Our forensic inspections identify the root cause.", to: "/inspections", tag: "Service" },
            { title: "Howell Reclamation Project", desc: "See how we restored a century-old reclaimed barnwood floor using dust-controlled refinishing techniques.", to: "/projects/howell-reclaimed-tobacco-pine", tag: "Project" },
          ]}
        />

        <GoogleReviewsCarousel contextTags={["refinishing"]} count={4} />
        <PageFAQ faqs={refinishingFAQs} heading={<>Refinishing questions <em className="italic font-normal text-primary">answered.</em></>} subheading="Refinishing FAQs" suppressJsonLd />
      </main>
      <SiteFooter />
    </div>
  );
};

export default Refinishing;
